import CheckIcon from "@mui/icons-material/Check";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { FormEvent } from "react";
import type { AuthHeaderIconKey } from "./loginNoteIcons";
import { AuthSuccessContent } from "./loginSuccessUi";
import {
  getLoginFieldSx,
  getLoginFooterLinkBoxSx,
  getLoginPrimaryButtonSx,
  getLoginSecondaryLinkSx,
  LoginFormHeader,
  loginFormPaddingSx,
} from "./loginUi";

export type LoginFormProps = {
  onSubmit?: (email: string, password: string) => void;
  error?: string;
  loading?: boolean;
  success?: boolean;
  forgotPasswordUrl?: string;
  tenantName?: string;
  headerIcon?: AuthHeaderIconKey;
  translations: {
    welcomeBack: string;
    signInTitle: string;
    signInSubtitle: string;
    signIn: string;
    email: string;
    password: string;
    emailPlaceholder: string;
    forgotPassword: string;
    fieldRequired: string;
    invalidEmail: string;
  };
};

export const LoginForm = ({
  onSubmit,
  error,
  loading = false,
  success = false,
  forgotPasswordUrl = "/accounts/password_reset/",
  tenantName,
  headerIcon,
  translations: t,
}: LoginFormProps) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const validate = (): boolean => {
    let valid = true;
    setEmailError("");
    setPasswordError("");

    if (!email.trim()) {
      setEmailError(t.fieldRequired);
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t.invalidEmail);
      valid = false;
    }

    if (!password) {
      setPasswordError(t.fieldRequired);
      valid = false;
    }

    return valid;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit?.(email.toLowerCase(), password);
    }
  };

  const fieldSx = getLoginFieldSx(theme);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{ ...loginFormPaddingSx, textAlign: "left" }}
      data-testid="login-form"
    >
      <AuthSuccessContent
        success={success}
        contentTestId="login-form-success-content"
        indicatorTestId="login-success-indicator"
      >
        <LoginFormHeader
          title={tenantName ? t.welcomeBack : t.signInTitle}
          subtitle={t.signInSubtitle}
          headerIcon={headerIcon}
        />

        {error ? (
          <Alert severity="error" sx={{ mb: 2.5 }} data-testid="login-error">
            {error}
          </Alert>
        ) : null}

        <TextField
          fullWidth
          label={t.email}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          placeholder={t.emailPlaceholder}
          autoComplete="email"
          autoFocus
          sx={fieldSx}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              inputProps: { "data-testid": "login-email" },
            },
          }}
        />

        <TextField
          fullWidth
          label={t.password}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!passwordError}
          helperText={passwordError}
          autoComplete="current-password"
          sx={{ ...fieldSx, mb: 3 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              inputProps: { "data-testid": "login-password" },
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || success}
          data-testid="login-submit"
          sx={getLoginPrimaryButtonSx(theme)}
        >
          {success ? (
            <CheckIcon fontSize="small" />
          ) : loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t.signIn
          )}
        </Button>

        <Box sx={getLoginFooterLinkBoxSx()}>
          <Link
            href={forgotPasswordUrl}
            underline="hover"
            color="text.secondary"
            sx={getLoginSecondaryLinkSx()}
            data-testid="forgot-password-link"
          >
            {t.forgotPassword}
          </Link>
        </Box>
      </AuthSuccessContent>
    </Box>
  );
};
